import React, { useState, useMemo } from "react";
import GenericModal from "./GenericModal";
import { dollarFormat } from "../../utils/formatting";

const SplitTipCalculatorModal = () => {
    const [serviceSubtotal, setServiceSubtotal] = useState("");
    const [taxAmount, setTaxAmount] = useState("");
    const [totalCharged, setTotalCharged] = useState("");
    const [groomers, setGroomers] = useState([
        { id: 1, name: "", lineItems: [""] },
        { id: 2, name: "", lineItems: [""] },
    ]);

    const parseNumber = (val) => {
        const cleaned = String(val).replace(/[$,]/g, "");
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
    };

    const calculations = useMemo(() => {
        const subtotal = parseNumber(serviceSubtotal);
        const tax = parseNumber(taxAmount);
        const total = parseNumber(totalCharged);
        
        const tip = total - subtotal - tax;
        
        const groomerTotals = groomers.map(g => {
            const lineItemValues = g.lineItems.map(item => parseNumber(item));
            const serviceValue = lineItemValues.reduce((sum, val) => sum + val, 0);
            return {
                ...g,
                lineItemValues,
                serviceValue
            };
        });
        
        const totalServices = groomerTotals.reduce((sum, g) => sum + g.serviceValue, 0);
        
        const groomerShares = groomerTotals.map(g => {
            const proportion = totalServices > 0 ? g.serviceValue / totalServices : 0;
            const tipShare = tip * proportion;
            return {
                ...g,
                proportion,
                tipShare,
            };
        });
        
        return {
            subtotal,
            tax,
            total,
            tip,
            totalServices,
            groomerShares,
            isValid: total > 0 && tip >= 0 && totalServices > 0,
            servicesMatchSubtotal: Math.abs(totalServices - subtotal) < 0.01,
        };
    }, [serviceSubtotal, taxAmount, totalCharged, groomers]);

    const addGroomer = () => {
        const newId = Math.max(...groomers.map(g => g.id), 0) + 1;
        setGroomers([...groomers, { id: newId, name: "", lineItems: [""] }]);
    };

    const removeGroomer = (id) => {
        if (groomers.length > 2) {
            setGroomers(groomers.filter(g => g.id !== id));
        }
    };

    const updateGroomer = (id, field, value) => {
        setGroomers(groomers.map(g => 
            g.id === id ? { ...g, [field]: value } : g
        ));
    };

    const updateLineItem = (groomerId, itemIndex, value) => {
        setGroomers(groomers.map(g => {
            if (g.id !== groomerId) return g;
            const newLineItems = [...g.lineItems];
            newLineItems[itemIndex] = value;
            return { ...g, lineItems: newLineItems };
        }));
    };

    const addLineItem = (groomerId) => {
        setGroomers(groomers.map(g => {
            if (g.id !== groomerId) return g;
            return { ...g, lineItems: [...g.lineItems, ""] };
        }));
    };

    const removeLineItem = (groomerId, itemIndex) => {
        setGroomers(groomers.map(g => {
            if (g.id !== groomerId) return g;
            if (g.lineItems.length <= 1) return g;
            const newLineItems = g.lineItems.filter((_, idx) => idx !== itemIndex);
            return { ...g, lineItems: newLineItems };
        }));
    };

    const resetForm = () => {
        setServiceSubtotal("");
        setTaxAmount("");
        setTotalCharged("");
        setGroomers([
            { id: 1, name: "", lineItems: [""] },
            { id: 2, name: "", lineItems: [""] },
        ]);
    };

    return (
        <GenericModal
            name="splitTipCalculatorModal"
            title="Split Tip Calculator"
            hideButton={true}
            modalMaxWidth="max-w-lg"
            stickyFooter={
                <div className="flex justify-end">
                    <button
                        onClick={resetForm}
                        className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                    >
                        Reset
                    </button>
                </div>
            }
        >
            <div className="flex flex-col gap-4 p-2">
                <div className="grid grid-cols-3 gap-3">
                    <label className="block text-sm font-medium text-gray-700">
                        Subtotal
                        <input
                            type="text"
                            value={serviceSubtotal}
                            onChange={(e) => setServiceSubtotal(e.target.value)}
                            placeholder="$0.00"
                            className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                        />
                    </label>
                    <label className="block text-sm font-medium text-gray-700">
                        Tax
                        <input
                            type="text"
                            value={taxAmount}
                            onChange={(e) => setTaxAmount(e.target.value)}
                            placeholder="$0.00"
                            className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                        />
                    </label>
                    <label className="block text-sm font-medium text-gray-700">
                        Total Charged
                        <input
                            type="text"
                            value={totalCharged}
                            onChange={(e) => setTotalCharged(e.target.value)}
                            placeholder="$0.00"
                            className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                        />
                    </label>
                </div>

                <div className="bg-indigo-50 border border-indigo-100 p-3 rounded-xl text-center">
                    <span className="text-sm font-medium text-gray-600">Calculated Tip: </span>
                    <span className={`text-xl font-bold ${calculations.tip < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {dollarFormat(calculations.tip)}
                    </span>
                    {calculations.tip < 0 && (
                        <p className="text-red-500 text-xs mt-1">
                            Total charged is less than subtotal + tax
                        </p>
                    )}
                </div>

                <div className="pt-2">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Groomers</h3>
                        <button 
                            onClick={addGroomer}
                            className="!px-3 !py-1.5 !text-xs !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-lg !transition-colors"
                        >
                            + Add Groomer
                        </button>
                    </div>

                    <div className="flex flex-col gap-3">
                        {groomers.map((groomer, index) => (
                            <div key={groomer.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                                <div className="flex gap-2 items-center mb-2">
                                    <input
                                        type="text"
                                        value={groomer.name}
                                        onChange={(e) => updateGroomer(groomer.id, "name", e.target.value)}
                                        placeholder={`Groomer ${index + 1} name`}
                                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition text-sm"
                                    />
                                    {groomers.length > 2 && (
                                        <button
                                            onClick={() => removeGroomer(groomer.id)}
                                            className="!px-2 !py-1.5 !text-xs !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-red-50 hover:!border-red-200 hover:!text-red-600 !text-gray-400 !transition-colors"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2 items-center">
                                    {groomer.lineItems.map((item, itemIdx) => (
                                        <div key={itemIdx} className="flex items-center gap-1">
                                            <input
                                                type="text"
                                                value={item}
                                                onChange={(e) => updateLineItem(groomer.id, itemIdx, e.target.value)}
                                                placeholder="$0.00"
                                                className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg bg-white focus:border-indigo-400 focus:ring-1 focus:ring-indigo-100 outline-none transition text-sm"
                                            />
                                            {groomer.lineItems.length > 1 && (
                                                <button
                                                    onClick={() => removeLineItem(groomer.id, itemIdx)}
                                                    className="text-gray-400 hover:text-red-500 text-sm px-1 transition-colors"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => addLineItem(groomer.id)}
                                        className="!px-2 !py-1 !text-xs !bg-white !border !border-gray-200 !rounded-lg hover:!bg-gray-100 !text-gray-500 !transition-colors"
                                    >
                                        + Add
                                    </button>
                                    <span className="ml-auto text-xs font-semibold text-gray-500">
                                        {dollarFormat(groomer.lineItems.reduce((sum, item) => sum + parseNumber(item), 0))}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!calculations.servicesMatchSubtotal && calculations.totalServices > 0 && (
                        <p className="text-amber-600 text-xs mt-2">
                            ⚠ Groomer services ({dollarFormat(calculations.totalServices)}) don't match subtotal ({dollarFormat(calculations.subtotal)})
                        </p>
                    )}
                </div>

                {calculations.isValid && (
                    <div className="pt-2">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Tip Split Results</h3>
                        <div className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Groomer</th>
                                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Service</th>
                                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Share</th>
                                        <th className="text-right px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Tip</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {calculations.groomerShares
                                        .filter(g => g.serviceValue > 0)
                                        .map((g, idx) => (
                                        <tr key={g.id} className="border-b border-gray-50 hover:bg-indigo-50/30 transition-colors">
                                            <td className="px-3 py-2 font-medium text-gray-700">{g.name || `Groomer ${idx + 1}`}</td>
                                            <td className="text-right px-3 py-2 text-gray-600">{dollarFormat(g.serviceValue)}</td>
                                            <td className="text-right px-3 py-2 text-gray-500">{(g.proportion * 100).toFixed(1)}%</td>
                                            <td className="text-right px-3 py-2 font-bold text-emerald-600">
                                                {dollarFormat(g.tipShare)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100/50 font-semibold">
                                        <td className="px-3 py-2 text-gray-700">Total</td>
                                        <td className="text-right px-3 py-2 text-gray-600">{dollarFormat(calculations.totalServices)}</td>
                                        <td className="text-right px-3 py-2 text-gray-500">100%</td>
                                        <td className="text-right px-3 py-2 text-emerald-600">{dollarFormat(calculations.tip)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </GenericModal>
    );
};

export default SplitTipCalculatorModal;
